import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, Search, Award, Gift, ArrowLeft, Cake } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MonSoldePage = () => {
  const [email, setEmail] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const lookup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true); setError(''); setData(null);
    try {
      const { data } = await axios.get(`${API}/loyalty/balance?email=${encodeURIComponent(email)}`);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E120D] via-[#5C2018] to-[#2E120D] py-12 px-4">
      <div className="max-w-xl mx-auto">
        <Link
          to="/fidelite"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FDFAF4]/70 hover:text-[#B8922A] mb-6 transition-colors"
          data-testid="solde-back"
        >
          <ArrowLeft size={14} /> Revenir au Cercle Harmony
        </Link>

        <div className="text-center mb-8">
          <Award size={32} className="text-[#B8922A] mx-auto mb-3" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2">Cercle Harmony · Espace fidélité</p>
          <h1
            className="text-5xl text-[#FDFAF4] mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            data-testid="solde-title"
          >
            Mon solde de points
          </h1>
          <p className="text-sm text-[#FDFAF4]/80 italic">
            Saisissez votre email pour consulter vos points fidélité
          </p>
        </div>

        <form onSubmit={lookup} className="bg-[#FDFAF4] rounded-2xl p-6 shadow-[0_20px_60px_rgba(0,0,0,0.3)] mb-6">
          <label className="block text-xs uppercase tracking-[0.2em] text-[#5C2018] mb-2 font-medium">
            Votre email
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="prenom.nom@email.fr"
              required
              className="flex-1 p-3 border border-[#B8922A]/30 rounded-lg focus:outline-none focus:border-[#1E7A6A] text-sm"
              data-testid="solde-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary px-6 py-3 rounded-lg text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="solde-lookup-btn"
            >
              <Search size={14} /> {loading ? 'Recherche…' : 'Voir mon solde'}
            </button>
          </div>
          <p className="text-[11px] text-[#332724]/60 italic mt-3">
            🔒 Consultation simple sans mot de passe. Vous devez avoir été inscrit(e) au Cercle Harmony en boutique.
          </p>
        </form>

        {error && (
          <div className="bg-[#FDFAF4]/95 rounded-xl p-5 border-2 border-[#B8922A] text-center" data-testid="solde-error">
            <p className="text-sm text-[#5C2018] mb-3">{error}</p>
            <Link to="/fidelite" className="text-xs uppercase tracking-[0.2em] text-[#1E7A6A] underline">
              En savoir plus sur le Cercle Harmony
            </Link>
          </div>
        )}

        {data && (
          <div className="space-y-4" data-testid="solde-result">
            {/* Carte principale */}
            <div className="bg-[#FDFAF4] rounded-2xl p-7 shadow-[0_20px_60px_rgba(0,0,0,0.3)] text-center">
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">
                Bonjour {data.name}
              </p>
              <div className="my-6">
                <p className="text-7xl font-light text-[#1E7A6A] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                  {data.points}
                </p>
                <p className="text-xs uppercase tracking-[0.3em] text-[#332724]/70">
                  point{data.points > 1 ? 's' : ''} Harmony
                </p>
              </div>
              {data.current_reward_eur > 0 && (
                <div className="bg-[#B8922A]/15 border border-[#B8922A] rounded-xl p-4 mb-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Vous pouvez convertir</p>
                  <p className="text-3xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                    {data.current_reward_eur}€ offerts
                  </p>
                  <p className="text-[11px] text-[#332724]/70 italic mt-1">
                    À utiliser sur votre prochain achat en boutique
                  </p>
                </div>
              )}
              {data.next_palier && (
                <p className="text-xs text-[#332724]/80 mt-3">
                  🎯 Plus que <strong className="text-[#1E7A6A]">{data.next_palier.points_needed} points</strong> pour débloquer
                  <strong className="text-[#B8922A]"> {data.next_palier.reward_eur}€ offerts</strong>
                </p>
              )}
            </div>

            {/* Anniversaire */}
            {data.birthday_month_offer && (
              <div className="bg-gradient-to-br from-[#B8922A]/20 to-[#FDFAF4] rounded-2xl p-5 border border-[#B8922A] flex items-start gap-3" data-testid="solde-anniv">
                <Cake size={24} className="text-[#B8922A] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#5C2018] mb-1">Cadeau anniversaire actif</p>
                  <p className="text-xs text-[#332724]/80 font-light leading-relaxed">
                    Le mois de votre anniversaire, vous recevez automatiquement
                    <strong> -20% sur tout achat</strong> par email le 1ᵉʳ du mois.
                  </p>
                </div>
              </div>
            )}

            {/* Cumul dépensé */}
            <div className="bg-[#FDFAF4]/10 border border-[#FDFAF4]/20 rounded-xl p-4 text-center">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#FDFAF4]/70 mb-1">Cumul de vos achats Harmony</p>
              <p className="text-xl text-[#FDFAF4]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {Number(data.total_spent).toFixed(2)}€
              </p>
            </div>

            <p className="text-[11px] text-[#FDFAF4]/60 italic text-center pt-2">
              Une question sur votre solde ? Appelez-nous au <a href="tel:0467215708" className="underline hover:text-[#B8922A]">04 67 21 57 08</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonSoldePage;
